import clsx from 'clsx';
import { useField } from 'formik';
import React, { useEffect, useRef } from 'react';
import Switch from 'react-switch';

import { Theme } from '@interfaces/global';
import { SWITCH_BORDER_CLASSES, SWITCH_COLORS, TEXT_COLORS } from '@utils/constants';

export type SwitchTheme = Theme;

interface SwitchFieldProps {
  className?: string;
  colorTheme?: SwitchTheme;
  disabled?: boolean;
  height?: number;
  id?: string;
  label?: string;
  labelClassName?: string;
  name: string;
  onChangeCallBack?: ({ checked, id }: { checked: boolean; id?: string }) => void;
  switchClassName?: string;
  width?: number;
}

const SwitchField = ({
  className,
  colorTheme,
  disabled = false,
  height = 24,
  id,
  label,
  labelClassName,
  name,
  onChangeCallBack,
  switchClassName,
  width = 48,
}: SwitchFieldProps) => {
  const [field, , helpers] = useField(name);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onChangeCallBack) {
      onChangeCallBack({ checked: field.value, id });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  const OFF_COLOR =
    SWITCH_COLORS.background.off[colorTheme] ?? SWITCH_COLORS.background.off.default;
  const OFF_HANDLE_COLOR = SWITCH_COLORS.handle.off[colorTheme] ?? SWITCH_COLORS.handle.off.default;
  const ON_COLOR = SWITCH_COLORS.background.on[colorTheme] ?? SWITCH_COLORS.background.on.default;
  const ON_HANDLE_COLOR = SWITCH_COLORS.handle.on[colorTheme] ?? SWITCH_COLORS.handle.on.default;
  const getBorderClasses = () => {
    if (field.value) {
      return SWITCH_BORDER_CLASSES.on[colorTheme];
    } else {
      return SWITCH_BORDER_CLASSES.off[colorTheme];
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleToggle = (checked: boolean) => {
    helpers.setValue(checked);
  };

  return (
    <div
      id={`${id}-container`}
      className={clsx('flex items-center', className)}
      onClick={handleClick}
    >
      <Switch
        className={clsx('switch mr-8', switchClassName, getBorderClasses())}
        id={id}
        name={name}
        width={width}
        height={height}
        offColor={OFF_COLOR}
        offHandleColor={OFF_HANDLE_COLOR}
        onColor={ON_COLOR}
        onHandleColor={ON_HANDLE_COLOR}
        uncheckedIcon={false}
        checkedIcon={false}
        checked={field.value}
        disabled={disabled}
        onChange={handleToggle}
      />
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            'max-w-full font-suisse-intl text-icon-sm sm:max-w-[310px]',
            TEXT_COLORS[colorTheme],
            labelClassName
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
};

export default SwitchField;
